// 移植自 crm/src/utils.js 的純函式段（從 bpToPercent 到結尾，內容原樣複製，函式名稱相同，舊版 import { x } from '@/utils' 照用）。
// 舊版的 res2json／createFetchEffect／apiErrorMessage／firstErrorMessage 不搬：改用 @/lib/request、@/lib/apiError 與 TanStack Query。
// 時區常數與換算在 @/utils/time（舊版 lib/constants 的 TZ＝那邊的 LOCAL_TZ）。
import dayjs from 'dayjs'
import { LOCAL_TZ } from './time'

export const bpToPercent = (bp) => {
  const n = Number(bp ?? 0)
  return Number.isFinite(n) ? n / 100 : 0
}

export const percentToBp = (percent) => {
  const n = Number(percent ?? 0)
  return Number.isFinite(n) ? Math.round(n * 100) : 0 // 同反向換算：非數字回 0，不送 NaN
}

export const yuanToCents = (yuan) => {
  const n = Number(yuan ?? 0)
  return Number.isFinite(n) ? Math.round(n * 100) : 0 // 同反向換算：非數字回 0，不送 NaN
}

export const centsToYuan = (cents) => {
  const n = Number(cents ?? 0)
  return Number.isFinite(n) ? Math.round(n) / 100 : 0
}

export const fmtPercent = (bp) => {
  const v = bpToPercent(bp)
  const fixed = Number.isFinite(v) ? v.toFixed(2) : '0.00'
  return String(parseFloat(fixed))
}

export const fmtMoney = (cents) => {
  const v = centsToYuan(cents)
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export const fmtInt = (n) => (typeof n === 'number' ? n.toLocaleString() : n)
export const fmtDate = (iso) =>
  dayjs(iso).tz?.(LOCAL_TZ)?.format?.('YYYY-MM-DD HH:mm:ss') ??
  dayjs(iso).format('YYYY-MM-DD HH:mm:ss')

export const remainingDays = (iso) => dayjs(iso).startOf('day').diff(dayjs().startOf('day'), 'day')

// 內容類型標記
const typeMarkerRegex = /<!-- type:(WYSIWYG|Markdown) -->$/
const wysiwygMarker = '<!-- type:WYSIWYG -->'
const markdownMarker = '<!-- type:Markdown -->'

/**
 * 取得內容的類型標記
 * @param {string} content - 內容
 * @returns {'WYSIWYG'|'Markdown'|null} - 類型或 null
 */
export const getContentType = (content) => {
  if (!content || typeof content !== 'string') return null
  const match = content.match(typeMarkerRegex)
  return match ? match[1] : null
}

/**
 * 移除內容的類型標記
 * @param {string} content - 內容
 * @returns {string} - 移除標記後的內容
 */
export const stripContentType = (content) => {
  if (!content || typeof content !== 'string') return content
  return content.replace(typeMarkerRegex, '').trimEnd()
}

/**
 * 添加類型標記到內容
 * @param {string} content - 內容
 * @param {'WYSIWYG'|'Markdown'} type - 類型
 * @returns {string} - 帶標記的內容
 */
export const addContentType = (content, type) => {
  if (!content || typeof content !== 'string') return content
  // 先移除舊標記
  const clean = stripContentType(content)
  const marker = type === 'WYSIWYG' ? wysiwygMarker : markdownMarker
  return `${clean}\n${marker}`
}

/**
 * 檢測內容是否為 WYSIWYG 格式
 * @param {string} content - 要檢測的內容
 * @returns {boolean} - 是否為 WYSIWYG
 */
export const isHtmlContent = (content) => {
  return getContentType(content) === 'WYSIWYG'
}

/**
 * 檢測內容是否為 Markdown 格式
 * @param {string} content - 要檢測的內容
 * @returns {boolean} - 是否為 Markdown
 */
export const isMarkdown = (content) => {
  return getContentType(content) === 'Markdown'
}

/**
 * 根據檔名取得檔案類型
 * @param {string} filename - 檔案名稱
 * @returns {'image'|'pdf'|'video'|'audio'|'other'} - 檔案類型
 */
export const getFileType = (filename) => {
  if (!filename) return 'other'
  const ext = filename.split('.').pop()?.toLowerCase()

  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico']
  const videoExts = ['mp4', 'webm', 'ogg', 'mov', 'avi']
  const audioExts = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a']
  const pdfExts = ['pdf']

  if (imageExts.includes(ext)) return 'image'
  if (pdfExts.includes(ext)) return 'pdf'
  if (videoExts.includes(ext)) return 'video'
  if (audioExts.includes(ext)) return 'audio'
  return 'other'
}

/**
 * 檢查檔案是否可預覽
 * @param {string} filename - 檔案名稱
 * @returns {boolean} - 是否可預覽
 */
export const isPreviewable = (filename) => {
  const type = getFileType(filename)
  return ['image', 'pdf', 'video', 'audio'].includes(type)
}

/**
 * 取得附件的 URL
 * 圖片使用 /images/ 前綴，其他使用 /files/ 前綴
 * @param {string} filepath - 檔案路徑
 * @param {string} filename - 檔案名稱（用於判斷類型）
 * @returns {string} - 完整 URL
 */
export const getAttachmentUrl = (filepath, filename) => {
  if (!filepath) return ''
  const type = getFileType(filename)
  const prefix = type === 'image' ? '/images' : '/files'
  return `${prefix}/${filepath}`
}

/**
 * 取得圖片縮圖 URL
 * @param {string} filepath - 檔案路徑
 * @param {Object} options - 縮圖選項
 * @param {number} options.size - 尺寸（默認 200）
 * @param {'scale'|'crop'} options.mode - 模式：scale 保持比例縮放，crop 裁切為正方形
 * @returns {string} - 縮圖 URL
 */
export const getImageThumbnailUrl = (filepath, options = {}) => {
  if (!filepath) return ''
  const { size = 200, mode = 'crop' } = options
  const suffix = mode === 'scale' ? `/r${size}` : `/${size}`
  return `/images/${filepath}${suffix}`
}

// 把翻譯字串裡的 {name} 佔位符換成 JSX 片段，回傳可直接放進 JSX 的陣列。
//
// i18n 的 format() 只做字串替換，帶不了 JSX，所以需要粗體／色字的句子過去都被拆成
// 多個 key 拼接（'…as CSV; the ' + <b>範圍</b> + 'is based on the ' + …）。那樣做語序
// 被寫死成英文的順序，語序不同的語言無法正確翻譯，譯者拿到的也是一堆沒有上下文的碎片。
// 改成一個完整句子的 key，佔位符在這裡換成 JSX。
//   tParts(t(ns, '… the {scope} is based on the {filters}.'), { scope: <b>…</b>, filters: <b>…</b> })
//   （示範用 ns 變數而非字面 'line'：本檔自 2026-09-02 引入 @/i18n 後會被 i18n:check 掃到，字面 ns 會把註解誤判成缺 key）
export function tParts(text, parts) {
  return String(text).split(/(\{[A-Za-z_][A-Za-z0-9_]*\})/).map((seg) => {
    const m = /^\{([A-Za-z_][A-Za-z0-9_]*)\}$/.exec(seg)
    return m ? (parts[m[1]] ?? seg) : seg
  })
}
